/**
 * Google Maps Helper
 *
 * Utilities for initializing Google Maps, managing markers, and handling
 * map interactions. Designed to be used from Stimulus controllers.
 *
 * Usage:
 *   import { initMap, createMarker, setupMarkerListeners } from '../utils/google_maps_helper'
 *
 *   connect() {
 *     this.map = initMap(this.mapdivTarget, center, zoom)
 *   }
 */

import Logger from '../lib/logger'

const SAO_PAULO_CENTER = { lat: -23.55, lng: -46.63 }

let debugIdCounter = 0
/** Generate a simple debug ID for tracking map instances */
function generateDebugId() {
  return `map-${++debugIdCounter}`
}

/** Check if Google Maps API has loaded globally */
export function isGoogleMapsLoaded() {
  return typeof window.google !== 'undefined'
}

/**
 * Initialize a Google Maps instance on a container element
 *
 * @param {HTMLElement} container - DOM element to render map into
 * @param {Object} options - Map options: center, zoom
 * @returns {google.maps.Map|null} Map instance or null if API not loaded
 */
export function initMap(container, { center = SAO_PAULO_CENTER, zoom = 4 } = {}) {
  if (!isGoogleMapsLoaded()) {
    Logger.warn('[Maps] Google Maps API not loaded')
    return null
  }

  if (container.childElementCount > 0) {
    Logger.debug('[Maps] Clearing non-empty map container')
    container.innerHTML = ''
  }

  container.dataset.mapInitialized = 'true'

  const map = new window.google.maps.Map(container, {
    center,
    zoom
  })

  Logger.debug('[Maps] Map instance created', { container: container.id, zoom })
  return map
}

/**
 * Create a draggable marker on the map
 *
 * @param {google.maps.Map} map - Map instance
 * @param {Object} position - { lat, lng }
 * @returns {google.maps.Marker|null} Marker instance or null if API not loaded
 */
export function createMarker(map, position) {
  if (!isGoogleMapsLoaded() || !map) return null

  return new window.google.maps.Marker({
    position,
    animation: window.google.maps.Animation.DROP,
    map,
    draggable: true
  })
}

/**
 * Remove marker from map
 */
export function removeMarker(marker) {
  if (marker) {
    marker.setMap(null)
  }
}

/**
 * Configure map click handler for coordinate picking
 *
 * @param {google.maps.Map} map - Map instance
 * @param {Function} onPositionSelected - Callback: ({ lat, lng }) => void
 */
export function setupMapClick(map, onPositionSelected) {
  map.addListener('click', (event) => {
    onPositionSelected({
      lat: event.latLng.lat(),
      lng: event.latLng.lng()
    })
  })
}

/**
 * Configure marker drag listeners for live coordinate updates
 *
 * @param {google.maps.Marker} marker - Marker instance (must exist)
 * @param {Function} onDrag - Callback during drag: ({ lat, lng }) => void
 * @param {google.maps.Map} map - Map to pan after drag ends (optional)
 */
export function setupMarkerListeners(marker, onDrag, map) {
  marker.addListener('drag', () => {
    const pos = marker.getPosition()
    onDrag({ lat: pos.lat(), lng: pos.lng() })
  })

  if (map) {
    marker.addListener('dragend', () => {
      map.panTo(marker.getPosition())
    })
  }
}

/**
 * Center and zoom map on a marker position
 */
export function centerOnMarker(map, marker) {
  if (!map || !marker) return

  map.setCenter(marker.getPosition())
  map.setZoom(18)
}

/**
 * Cleanup map instance and container
 */
export function cleanupMap(container) {
  if (container) {
    container.innerHTML = ''
    delete container.dataset.mapInitialized
  }
}
